/**
 * usePortalOpen — the ONE implementation of "open the Stripe customer
 * portal, and render whatever the backend says when it can't" — shared by
 * CheckoutFlow's already-entitled result and EntitlementStatusBlock's
 * persistent "Manage billing" action.
 *
 * EXTRACTED AFTER A REAL DRIFT (F14-PR3.1, review finding B1, HIGH): each
 * component had its OWN copy of this, and CheckoutFlow's collapsed EVERY
 * portal-session failure — including the 404 — to one hardcoded retry line
 * ("We couldn't open billing management just now. Please try again.").
 * That 404 is NOT a defensive-only edge case: a guardian-granted student
 * hitting `already_entitled` has NO `billing_customer` row of their own —
 * the GUARDIAN is the Stripe customer (`create_portal_session`'s whole
 * design) — so `POST /billing/portal-session` 404s for them EVERY time,
 * and CheckoutFlow's old copy told them "try again" forever. The backend
 * deliberately raises this 404 as a `CheckoutRefused` (warm `message` +
 * `next_steps: ["Choose a plan to get started."]`) for exactly this
 * reachable case; EntitlementStatusBlock already rendered SOMETHING
 * reasonable for it (a hardcoded string that happened to roughly match),
 * but that was itself a second, independently-drifting copy of the same
 * "what do we say on a 404" decision. One hook, one answer, both callers.
 *
 * `openPortal()` never distinguishes "billing is dark" from "no billing
 * account yet" in its OWN messaging (both are the SAME backend 404 by
 * construction — `_require_feature()` and the no-`billing_customer` branch
 * are indistinguishable to the client, and both want the identical warm
 * "nothing to manage yet" framing) — it renders the server's `message`/
 * `next_steps` VERBATIM (via `friendlyError`/`nextSteps`, this module's
 * standing rule), never a second competing hardcoded line, with only the
 * FALLBACK text (used when the server sent no message at all) distinguishing
 * the 404 case from a genuine fault.
 *
 * Returns `{ state, message, steps, openPortal, reset }`:
 *   state       'idle' | 'loading' | 'error'
 *   message     the backend's own message once state==='error' (empty otherwise)
 *   steps       the backend's own next_steps array (possibly [])
 *   openPortal  () => void — the click handler; redirects on success
 *   reset       () => void — clears state back to 'idle' (callers reset this
 *               alongside their own dismiss/phase-reset so a stale portal
 *               error doesn't linger into an unrelated later render)
 *
 * BFCACHE RECOVERY (F14-PR3.2, review S5): `openPortal()`'s success path is
 * a REAL outbound navigation (`window.location.href = portalUrl`), which
 * sets `state:'loading'` right before the browser starts unloading. If the
 * student then hits the BACK button, some browsers restore this exact page
 * (React state and all) from the back/forward cache instead of re-running
 * this hook from scratch — the "Manage billing" button would be frozen on
 * "Opening…" forever, a real dead end with no way to ever click it again.
 * The `pageshow` event's `persisted===true` flag is the one reliable signal
 * a bfcache restore happened (a fresh load never sets it); on that signal,
 * a STUCK 'loading' resets to 'idle' so the button is actionable again. Any
 * OTHER state ('idle', 'error') is left untouched — there's nothing stuck
 * to recover there.
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import {
  createPortalSession,
  friendlyError,
  isBillingDark,
  nextSteps,
} from './billingApi';

export function usePortalOpen() {
  const [state, setState] = useState('idle');
  const [message, setMessage] = useState('');
  const [steps, setSteps] = useState([]);
  // Unmount guard — CheckoutFlow.js's OLD inline openPortal had this; carry
  // it into the extracted hook too (a caller can unmount mid-request, e.g.
  // CheckoutFlow's own "Great, thanks" dismiss firing while a slow portal
  // request is still in flight).
  const unmountedRef = useRef(false);
  useEffect(() => {
    unmountedRef.current = false;
    return () => {
      unmountedRef.current = true;
    };
  }, []);

  const openPortal = useCallback(async () => {
    setState('loading');
    setMessage('');
    setSteps([]);
    try {
      const { portal_url: portalUrl } = await createPortalSession();
      if (unmountedRef.current) return;
      window.location.href = portalUrl;
    } catch (err) {
      if (unmountedRef.current) return;
      setState('error');
      const dark = isBillingDark(err) || err?.response?.status === 404;
      setMessage(
        friendlyError(
          err,
          dark
            ? "There's no billing account to manage yet."
            : "We couldn't open billing management just now.",
        ),
      );
      setSteps(nextSteps(err));
    }
  }, []);

  const reset = useCallback(() => {
    setState('idle');
    setMessage('');
    setSteps([]);
  }, []);

  // Bfcache recovery (review S5) — see the file header. Only fires on a
  // RESTORE (event.persisted), never on a normal/first load.
  useEffect(() => {
    function handlePageShow(event) {
      if (!event.persisted) return;
      setState((prev) => (prev === 'loading' ? 'idle' : prev));
    }
    window.addEventListener('pageshow', handlePageShow);
    return () => window.removeEventListener('pageshow', handlePageShow);
  }, []);

  return { state, message, steps, openPortal, reset };
}
